<template>
  <v-app>
    <section class="section-edit">
      <h2>Edit User Group {{ this.userGroup.title }}</h2>

      <v-dialog
        v-model="loading"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            Loading...
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- CREATION ALERT -->
      <v-snackbar
        v-model="snackbar"
        :color="snackBarColor"
        top
        right
        :timeout=3000
      >
        <v-icon dark left>mdi-check</v-icon>
        {{ snackbarMessage }} {{ snackBarColor === 'error' ? '' : countDown }}
        <v-btn
          dark
          text
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>

      <!-- ADD USER CHILDRENS CONFIRMATION PANEL -->
      <template>
        <v-row justify="center">
          <v-dialog v-model="alert" max-width="600px">
            <v-card class="mx-auto align-end">
              <v-card-title>Add {{ userChildrens.userName }} and his {{ userChildrens.childrens.length }} children to this User group?</v-card-title>
              <v-card-actions>
                <v-spacer/>
                <v-btn
                  color="error"
                  dark
                  class="mr-1 mb-2"
                  @click="closeAlert"
                >
                  <v-icon
                    size="large"
                    color="white"
                    class="ml-2"
                    left
                  >
                    mdi-arrow-left
                  </v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  class="mr-5 mb-2"
                  dark
                  @click="addUserAndChildrenInGroup"
                >
                  <v-icon
                    size="large"
                    color="white"
                    class="ml-2"
                    left
                  >
                    mdi-plus
                  </v-icon>
                  Add
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </template>

      <v-text-field
        v-model="userGroup.title"
        label="Title*"
        single-line
        hide-details
        placeholder="New User Group"
      />

      <v-card class="mt-10">
        <v-card-title>
          <p>Do you want this group to be dynamic ?</p>
        </v-card-title>

        <v-card-text>
          <v-switch
            v-model="userGroup.refreshType"
            :label="`${userGroup.refreshType ? 'Yes' : 'No'}`"
          ></v-switch>

          <p v-if="userGroup.refreshType">Group will be dynamic. User list will be updated, if a user is added in the database.</p>
          <p v-else>Group will be fix. User list will NEVER be updated.</p>
        </v-card-text>
      </v-card>

      <v-card class="notification-table mt-10">
        <v-card-title>
          <v-row justify="space-between">
            <v-col cols="5">
              {{ pagination.total }} Users
            </v-col>

            <v-col cols="7">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search users by Username or Mail"
                single-line
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-title>

        <v-data-table
          v-if="users"
          :headers="headers"
          :items="users"
          :loading="isLoading"
          loading-text="Loading... Please wait"
          :server-items-length="pagination.total"
          :options.sync="options"
          :footer-props="footerProps"
        >
          <template v-slot:item.inGroup="{ item }">
            <v-chip :color="userInGroup.includes(item.userId) ? 'green' : 'grey lighten-1'" dark>
              {{ userInGroup.includes(item.userId) ? 'Yes' : 'No' }}
            </v-chip>
          </template>
          <template v-slot:item.username="{ item }">
            <v-item-group>
              <v-item dark>
                <div>{{ item.username ? item.username : '--' }}</div>
              </v-item>
            </v-item-group>
          </template>
          <template v-slot:item.mail="{ item }">
            <v-item-group>
              <v-item dark>
                <div>{{ item.mail ? item.mail : '--' }}</div>
              </v-item>
            </v-item-group>
          </template>
          <template v-slot:item.langageId="{ item }">
            <v-item-group>
              <v-item dark>
                <div>{{ item.langageId ? getlangageString(item.langageId) : '--' }}</div>
              </v-item>
            </v-item-group>
          </template>
          <template v-slot:item.action="{ item }">
            <v-chip
              @click="addUserInGroup(item.userId, item.username, item.mail)"
              v-if="!userInGroup.includes(item.userId)"
              class="mr-2"
              color="blue"
            >
              <v-icon
                size="large"
                color="white"
              >
                mdi-plus
              </v-icon>
            </v-chip>
            <v-chip
              @click="openAlert(item.username, item.userId)"
              v-if="!userInGroup.includes(item.userId)"
              color="blue"
            >
              <v-icon
                size="large"
                color="white"
              >
                mdi-auto-download
              </v-icon>
            </v-chip>
            <v-chip
              @click="removeUserInGroup(item.userId)"
              v-if="userInGroup.includes(item.userId)"
              color="error"
            >
              <v-icon
                size="large"
                color="white"
              >
                mdi-minus
              </v-icon>
            </v-chip>
          </template>
          <template v-slot:no-data>
            <p class="mt-4">No users available</p>
          </template>
        </v-data-table>
      </v-card>

      <v-btn
        @click="redirectToBack"
        width="250"
        class="btn mt-6"
        color="error"
      >
        <v-icon dark left size="large">mdi-arrow-left</v-icon>
        Back
      </v-btn>
      <v-btn
        @click="saveUserGroup"
        width="250"
        class="btn mt-6 ml-3"
        color="primary"
      >
        Save usergroup
        <v-icon dark right size="large">mdi-content-save</v-icon>
      </v-btn>
    </section>
  </v-app>
</template>

<script>
  import userGroupsApi from '@/services/api/user-groups-api';
  import UserType from '../enum/user-type.enum';
  import RefreshType from '../enum/refresh-type.enum';

  export default {
    name: 'editGroup',
    data: () => ({
      search: '',
      dialog: false,
      pagination: {},
      options: {
        page: 0,
        itemsPerPage: 20
      },
      userGroup: {
        title: '',
        type: "group",
        users: [],
        refreshType: true,
      },
      users: [],
      userInGroup: [],
      headers: [
        { text: 'In Group', value: 'inGroup', sortable: false },
        { text: 'Username', value: 'username', sortable: false },
        { text: 'Mail', value: 'mail', sortable: false },
        { text: 'Language', value: 'langageId', sortable: false },
        { text: 'Actions', value: 'action', sortable: false }
      ],
      editedIndex: -1,
      isLoading: true,
      footerProps: {
        'items-per-page-options': [5, 10, 20, 50]
      },
      canWatch: false,
      snackbar: false,
      snackBarColor: 'success',
      countDown: 3,
      alert: false,
      snackbarMessage: null,
      userChildrens: {
        userName: null,
        userId: null,
        childrens: []
      },
      RefreshType,
      loading: false,
    }),

    async mounted() {
      // Chargement des user groups et de la pagination
      let data = await userGroupsApi.getAllUsers(0, this.options.itemsPerPage);
      this.users = data.data.data;
      this.pagination = data.data.pagination;

      if (this.users) {
        this.isLoading = false
      }
    },

    watch: {
      options: {
        async handler () {
          // On change de pagination et on affiche les 20 users suivant ou précédents
          if (this.canWatch) {
            this.loading = true;
            const data = await userGroupsApi.getAllUsers(((this.options.page - 1) * this.options.itemsPerPage), this.options.itemsPerPage, this.search);
            this.users = data.data.data;
            this.loading = false;
          }

          this.canWatch = true;
        }
      },
      search: {
        async handler () {
          const data = await userGroupsApi.getAllUsers(0, this.options.itemsPerPage, this.search);

          // Quand la chaine de recherche change, ramener à la page 1
          this.options.page = 1;

          // Si la chaine de recherche change, on met à jour les données complètes mais aussi la pagination pour avoir le nombre total d'utilisateurs à jour
          this.users = data.data.data;
          this.pagination = data.data.pagination;
        }
      }
    },

    methods: {
      getlangageString(langageId) {
        switch (langageId) {
          case 1: return 'English (en)';
          case 2: return 'Danish (da)';
          case 3: return 'German (de)';
          case 4: return 'Spanish (es)';
          case 5: return 'French (fr)';
          case 6: return 'Hungarian (hu)';
          case 7: return 'Italian (it)';
          case 8: return 'Dutch (nl)';
          case 9: return 'Polish (po)';
          default: return 'English (en)';
        }
      },

      addUserInGroup(id, username, mail) {
        // On ajoute dans le userGroup les users sélectionnés
        this.userGroup.users.push({ userId: id, userType: UserType.INDEPENDENT, username, mail });

        // On ajoute dans le userInGroup les users sélectionnés
        this.userInGroup.push(id);
      },

      async addUserAndChildrenInGroup() {
        // On ferme la modale
        this.alert = false;

        // On injecte dans userInGroup et userGroup le user sélectioné ainsi que ses enfants
        for (let i = 0; i < this.userChildrens.childrens.length; i++) {
          this.userGroup.users.push({
            userId: this.userChildrens.childrens[i].userId.toString(),
            userType: this.userChildrens.childrens[i].userId.toString() === `${this.userChildrens.userId}` ? UserType.PARENT : UserType.CHILD,
            username: this.userChildrens.childrens[i].username,
            mail: this.userChildrens.childrens[i].mail,
          });
          this.userInGroup.push(this.userChildrens.childrens[i].userId.toString());
        }

        // On vide les user's childrens
        this.userChildrens.childrens = [];

        // On affiche le message indiquant que le template à bien été crée
        this.snackbarMessage = 'Childrens have been added';
        this.snackBarColor = 'success';
        this.snackbar = true;
        this.countDownTimer();

        setTimeout(() => {
          this.countDown = 3
        }, 3300)
      },

      removeUserInGroup(id) {
        // On supprime les users avec leur index
        let removeUserGroupIndex = this.userGroup.users.map(item => {
          return item.userId;
        }).indexOf(id);

        this.userGroup.users.splice(removeUserGroupIndex, 1);

        // On supprime les users désélectionnés du userInGroup
        let removeSelectedUsersIndex = this.userInGroup.indexOf(id);
        this.userInGroup.splice(removeSelectedUsersIndex, 1);
      },

      async saveUserGroup() {
        this.loading = true;

        if (!this.userGroup.title) {
          this.snackbarMessage = 'Title is mandatory.';
          this.snackBarColor = 'error';
          this.snackbar = true;
          this.loading = false;
          return;
        }

        // Découper la liste de Users en groupe, pour éviter le "request payload entity too large"
        const users = [];
        const NB_USERS_PER_GROUP = 800;
        const nbGroups = Math.ceil(this.userGroup.users.length / NB_USERS_PER_GROUP);

        for (let i = 0; i < nbGroups; i += 1) {
          users.push(this.userGroup.users.slice(i * NB_USERS_PER_GROUP, (i + 1) * NB_USERS_PER_GROUP));
        }

        // Le premier groupe de Users crée le user group
        let tmpUserGroup = null;
        try {
          const firstUsers = users.shift();
          tmpUserGroup = await userGroupsApi.createUserGroup({
            ...this.userGroup,
            users: firstUsers,
            refreshType: this.userGroup.refreshType ? RefreshType.DYNAMIC : RefreshType.FIX,
          });
        } catch {
          this.loading = false;
          this.snackbarMessage = 'An error has occured. The UserGroup couldn\'t be created.';
          this.snackBarColor = 'error';
          this.snackbar = true;
          
          return;
        }

        for (const userList of users) { // eslint-disable-line
          try {
            // Pour les autres groupes de users on ajoute les users au groupe
            await userGroupsApi.addUsers({ users: userList }, tmpUserGroup.data.usergroupId);
          } catch {
            this.snackbarMessage = 'An error has occured. The UserGroup couldn\'t be created.';
            this.snackBarColor = 'error';
            this.snackbar = true;
          }
        }

        // On affiche le message indiquant que le user group a bien été créé
        this.snackbarMessage = 'UserGroup has been created. Redirection to the User Groups page in';
        this.snackbarColor = 'success';
        this.snackbar = true;
        this.countDownTimer();

        // On redirige vers le listing des user groups après 3s
        setTimeout(() => {
          this.$router.push({ name: 'UserGroups' });
        }, 3300);

        this.loading = false;
      },

      async openAlert(userName, userId) {
        let data = await userGroupsApi.getChildrens(userName);

        // On injecte dans le userChildrens le user sélectioné ainsi que ses enfants
        for (let i = 0; i < data.data.length; i++) {
          this.userChildrens.childrens.push({ userId: data.data[i].f1, username: data.data[i].f2, mail: data.data[i].f4 });
        }

        this.userChildrens.userName = userName;
        this.userChildrens.userId = userId;
        this.userChildrens.childrensCount = data.data.length;
        this.alert = true;
      },

      closeAlert() {
        // On ferme la modale
        this.alert = false;

        // On vide les user's childrens
        this.userChildrens.childrens = [];
      },

      redirectToBack() {
        this.$router.back();
      },

      countDownTimer() {
        if (this.countDown > 0) {
          setTimeout(() => {
            this.countDown -= 1;
            this.countDownTimer();
          }, 1000)
        }
      }
    }
  };
</script>
